// toast-container.component.ts
import { CommonModule } from '@angular/common';
import {
  Component,
  TemplateRef,
  OnDestroy,
  OnInit,
  inject, 
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ToastAction, ToastInfo, ToastService } from './toast.service';
import {
  ToastBodyComponent,
  ToastComponent
} from '@coreui/angular-pro';
import { TranslatePipe } from 'i18n';

@Component({
  selector: 'toast-container!',
  standalone: true,
  imports: [
    CommonModule,
    ToastComponent,
    ToastBodyComponent,
    TranslatePipe
  ],
  template: `
    <ng-container *ngFor="let toast of toasts(); let i = index">
      <c-toast
        [autohide]="false"
        [visible]="visible.get(toast)?.()"
        [class]="toast.class"
        (visibleChange)="onVisibleChange(toast, $event)">
        <c-toast-body class="d-flex justify-content-between align-items-center gap-3">
          <div class="flex-grow-1">
            @if (isTemplate(toast.body)) {
              <ng-container *ngTemplateOutlet="toast.body"></ng-container>
            } @else {
              {{ toast.body | translate }}
            }
          </div>
          <div class="d-flex gap-1" *ngIf="toast.actions?.length">
            <button *ngFor="let action of toast.actions"
                    type="button"
                    class="btn btn-secondary {{ action.class }}"
                    (click)="onAction(toast, action, $event)">
              {{ action.label | translate }}
            </button>
          </div>
        </c-toast-body>
      </c-toast>
    </ng-container>
  `,
  styles: [`
    :host { display: block; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToastContainerComponent implements OnInit, OnDestroy {
  private toastService = inject(ToastService);

  toasts = signal<ToastInfo[]>([]);
  visible = new Map<ToastInfo, WritableSignal<boolean>>();
  private subscription!: Subscription;
  private timers = new Map<ToastInfo, any>();

  ngOnInit() {
    this.subscription = this.toastService.toasts$.subscribe(toasts => {
        const newToasts = toasts.filter(t => !this.toasts().includes(t));
        newToasts.forEach(toast => {
            this.visible.set(toast, signal(false));
            this.startAutoHide(toast);
        });
        this.toasts.set(toasts);
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.timers.forEach(t => clearTimeout(t));
    this.timers.clear();
  }

  onAction(toast: ToastInfo, action: ToastAction, event: Event) {
    event.stopPropagation();
    action.callback();
    this.hideAndRemove(toast);
  }

  onVisibleChange(toast: ToastInfo, visible: boolean) {
    if (!visible) {
      clearTimeout(this.timers.get(toast));
      this.timers.delete(toast);
      this.hideAndRemove(toast);
    }
  }

  isTemplate(body: TemplateRef<any> | string): body is TemplateRef<any> {
    return body instanceof TemplateRef;
  }

  private startAutoHide(toast: ToastInfo) {
    // setTimeout(() => {
      this.visible.get(toast)?.set(true);
    // }, 0);
    if (!toast.autohide) return;
    const timer = setTimeout(() => {
      this.hideAndRemove(toast);
    }, toast.delay ?? 5000);

    this.timers.set(toast, timer);
  }

  private hideAndRemove(toast: ToastInfo) {
    this.visible.get(toast)?.set(false);
    setTimeout(() => {
      if (this.toasts().includes(toast)) {
        this.toastService.remove(toast);
        this.visible.delete(toast);
        this.timers.delete(toast);
      }
    }, 300);
  }
}